export type WeddingLanguage = "en" | "vi";

export const coupleNames = "Cát Lượng & Xuân Hương";

export const weddingContent = {
  en: {
    meta: {
      title: `${coupleNames} - Wedding`,
      description: `Join us on our special day. Share your wishes for ${coupleNames}.`,
    },
    hero: {
      eyebrowTop: "TOGETHER WITH THEIR FAMILIES",
      eyebrowBottom: "Right on Time - In the quiet, we found home",
      date: "17 January 2027",
      location: "White Palace Vo Van Kiet",
      cta: "SEND YOUR WISHES",
      guestPhotosCta: "GUEST CAMERA MOMENTS",
    },
    chapters: [
      {
        label: "Chapter 1",
        title: "The Intersection",
        subtitle: "We almost lived entire lives without meeting",
        description: [
          "Luong passed through Australia.",
          "Huong returned from England.",
          "Truc was in Malaysia.",
          "Phi came back from America.",
          "And somehow, in a way that is still hard to explain, all those straight lines met on one birthday night in Saigon.",
        ],
      },
      {
        label: "Chapter 2",
        title: "Participation",
        subtitle: "Love is showing up for things you don't understand",
        description: [
          "Love is not about liking the same things.",
          "It is about showing up in the things that never really belonged to you.",
          "Huong never liked long roads, motorbike trips, or the feeling of sitting behind someone for hours.",
          "Luong never liked raves, and he was never the person who danced the most in the crowd.",
          "But then Huong still showed up on Luong's roads.",
          "And Luong still showed up under Huong's lights.",
          "Not because we loved those things.",
          "But because we loved the person standing beside us.",
          "\"I don't like it, but I still go with you. We were never the same.",
          "It is just that every time we turned around, the other person was still there.\"",
        ],
      },
      {
        label: "Chapter 3",
        title: "The Quiet",
        subtitle: "Not every love story is loud",
        description: [
          "Some are built in ordinary days.",
          "In quiet rides home.",
          "In patient silences.",
          "In someone who stays long enough to become your peace.",
          "\"I used to think love had to be full of words.",
          "It had to explain. It had to prove. It had to be spoken out loud.",
          "Then I met someone who was not good at saying much at all. He was only good at staying.",
          "I used to think that silence was distance.",
          "Until I realized it was simply the way he stayed beside me - like our friends joke, he is my hard counter.\"",
        ],
      },
    ],
    story: {
      heading: "Our Love Story",
      milestones: [
        {
          year: "How We Met",
          title: "The Beginning",
          description:
            "The first chapter started with a simple meeting that slowly turned into something steady, warm, and meaningful.",
        },
        {
          year: "First Trip",
          title: "Our First Adventure",
          description:
            "Traveling together gave us some of our earliest memories and showed us how naturally we moved through life side by side.",
        },
        {
          year: "Proposal",
          title: "The Question",
          description:
            "One unforgettable moment turned our journey into a promise, and from that day the future became something we were building together.",
        },
        {
          year: "Wedding",
          title: "The Next Chapter",
          description:
            "Now we are preparing to celebrate our wedding day and begin this new chapter surrounded by family and friends.",
        },
      ],
    },
    details: {
      ceremony: "Ceremony",
      reception: "Reception",
      dateTime: "DATE & TIME",
      location: "LOCATION",
      detailsSoon: "Sunday, 17 January 2027",
      ceremonyVenue: "White Palace Vo Van Kiet",
      receptionVenue: "White Palace Võ Văn Kiệt",
      directionsSoon: "Open in Google Maps",
      directionsUrl: "https://maps.app.goo.gl/nyxm6kKTEt7Vn5RS8",
      receptionNote: "Welcome & photos at 17:30. Ceremony at 19:00, followed by dinner at 19:30.",
      dressCode: "Dress Code",
      dressCodeAccent: "Blush beige · Cream yellow · Black · Muted blue",
      dressCodeText:
        "Blush beige, cream yellow, black or muted blue. Wear something you feel comfortable celebrating in.",
    },
    schedule: {
      heading: "Wedding Day Timeline",
      events: [
        {
          time: "17:00",
          title: "Welcoming Guests",
          description: "We will be ready to welcome family and friends at White Palace Vo Van Kiet.",
        },
        {
          time: "18:00",
          title: "Wedding Starts",
          description: `${coupleNames} will celebrate the beginning of this new chapter with family and friends.`,
        },
      ],
    },
    wishes: {
      heading: "Share Your Wishes",
      formTitle: "Send Your Wishes",
      nameLabel: "Your Name",
      namePlaceholder: "Enter your name",
      messageLabel: "Your Message",
      messagePlaceholder: "Share your wishes for the happy couple...",
      submitIdle: "ADD WISHES",
      submitLoading: "SENDING...",
      success: "Thank you! Your wishes have been received.",
      searchLabel: "Search wishes",
      searchPlaceholder: "Search by name or message",
      loadError: "We couldn't load wedding wishes right now.",
      sendError: "We couldn't send your wish right now.",
      appCheckError:
        "Firebase App Check is not configured yet. Add the REACT_APP_FIREBASE_* values to enable protected wedding wishes.",
      empty: "No wishes matched your search yet.",
      likeAction: "Like",
    },
    gallery: {
      heading: "Our Moments",
    },
    guestPhotos: {
      heading: "Guest Camera Moments",
      votePrompt: "Every photo tells our story. Tap the heart to vote for your favorite moments.",
      intro:
        "A happy tear, a big laugh, a little dance-floor magic — open your camera and share the moments you love most. Let us see our wedding through your eyes!",
      placeholder: "Open the camera to take a new wedding moment.",
      openCamera: "OPEN CAMERA",
      takePhoto: "TAKE PHOTO",
      closeCamera: "CLOSE CAMERA",
      sendPhoto: "SEND PHOTO",
      sending: "SENDING...",
      retake: "RETAKE",
      sendSuccess: "Thank you! Your photo has been added.",
      loadError: "We couldn't load guest photos right now.",
      sendError: "We couldn't send your photo right now.",
      appCheckError:
        "Firebase App Check is not configured yet. Add the REACT_APP_FIREBASE_* values to enable protected guest photos.",
      cameraUnavailable: "Camera is not available on this device or browser.",
      cameraPermissionError:
        "Camera permission was blocked. Please allow camera access to take a photo.",
      captureError: "We couldn't capture the photo. Please try again.",
      empty: "No guest photos yet. Be the first to capture a moment.",
      previewAlt: "Captured wedding moment preview",
      guestPhotoAlt: "Wedding guest moment",
    },
    footer: {
      date: "17 January 2027",
      location: "White Palace Vo Van Kiet",
      message: "We can't wait to celebrate with you.",
      backToTop: "Back to Top",
      sendWishes: "Send Wishes",
      copyright: `© 2026 ${coupleNames}. All rights reserved.`,
    },
  },
  vi: {
    meta: {
      title: `${coupleNames} - Lễ Cưới`,
      description: `Trân trọng mời bạn đến chung vui và gửi lời chúc đến ${coupleNames}.`,
    },
    hero: {
      eyebrowTop: "CÙNG GIA ĐÌNH HAI BÊN",
      eyebrowBottom: "TRÂN TRỌNG BÁO HỶ",
      date: "17 tháng 01 năm 2027",
      location: "White Palace Võ Văn Kiệt",
      cta: "GỬI LỜI CHÚC",
      guestPhotosCta: "KHOẢNH KHẮC CAMERA",
    },
    chapters: [
      {
        label: "Chương 1",
        title: "Giao Điểm",
        subtitle: "Chúng mình suýt đã sống trọn những cuộc đời không gặp nhau",
        description: [
          "Lượng đi qua Úc.",
          "Hương trở về từ Anh.",
          "Trúc ở Malaysia.",
          "Phi từ Mỹ quay về.",
          "Và bằng một cách rất khó giải thích, tất cả những đường thẳng đó gặp nhau trong một đêm sinh nhật ở Sài Gòn.",
        ],
      },
      {
        label: "Chương 2",
        title: "Có Mặt",
        subtitle: "Tình yêu là xuất hiện trong những điều mình không hiểu hết",
        description: [
          "Tình yêu không phải là thích cùng một thứ.",
          "Mà là xuất hiện trong những điều vốn không thuộc về mình.",
          "Hương không thích những cung đường dài, những chuyến phượt bằng xe máy, hay cảm giác ngồi sau lưng ai đó hàng giờ liền.",
          "Lượng không thích rave, và cũng chẳng bao giờ là người nhảy nhiều nhất trong đám đông.",
          "Nhưng rồi Hương vẫn có mặt trên những cung đường của Lượng.",
          "Và Lượng vẫn có mặt dưới những cuộc vui của Hương.",
          "Không phải vì mình yêu những điều đó.",
          "Mà vì mình yêu người đang đứng cạnh mình.",
          "\"Anh không thích, nhưng anh vẫn đi cùng em. Tụi mình chưa bao giờ giống nhau.",
          "Chỉ là mỗi lần quay đầu lại, đều thấy người kia vẫn ở đó.\"",
        ],
      },
      {
        label: "Chương 3",
        title: "Sự Yên Tĩnh",
        subtitle: "Không phải chuyện tình nào cũng ồn ào",
        description: [
          "Có những chuyện tình được xây bằng những ngày rất bình thường.",
          "Bằng những chuyến xe yên lặng về nhà.",
          "Bằng những khoảng im lặng kiên nhẫn.",
          "Bằng một người ở lại đủ lâu để trở thành sự bình yên của mình.",
          "\"Em từng nghĩ tình yêu phải thật nhiều lời.",
          "Phải giải thích. Phải chứng minh. Phải nói ra.",
          "Rồi em gặp một người chẳng giỏi nói điều gì cả. Chỉ giỏi ở lại.",
          "Em từng nghĩ sự im lặng ấy là khoảng cách.",
          "Cho đến khi nhận ra, đó đơn giản chỉ là cách anh ở cạnh em - như bạn bè hay nói đùa rằng anh là khắc chế cứng của em.\"",
        ],
      },
    ],
    story: {
      heading: "Chuyện Tình Của Chúng Mình",
      milestones: [
        {
          year: "Ngày Gặp",
          title: "Ngày Mình Gặp Nhau",
          description:
            "Mọi chuyện bắt đầu từ một lần gặp gỡ rất bình dị, rồi dần trở thành một hành trình đầy yêu thương, tin tưởng và gắn bó.",
        },
        {
          year: "Chuyến Đi Đầu",
          title: "Chuyến Đi Đầu Tiên",
          description:
            "Những chuyến đi đầu tiên đã mang đến cho chúng mình thật nhiều kỷ niệm đẹp và giúp cả hai hiểu nhau hơn trên từng chặng đường.",
        },
        {
          year: "Lời Cầu Hôn",
          title: "Khoảnh Khắc Đặc Biệt",
          description:
            "Một câu hỏi giản dị nhưng đầy ý nghĩa đã biến hành trình yêu thương thành một lời hứa cho tương lai của cả hai.",
        },
        {
          year: "Ngày Cưới",
          title: "Chương Mới Bắt Đầu",
          description:
            "Giờ đây, chúng mình đang chuẩn bị cho ngày cưới và háo hức bước vào chương mới của cuộc sống cùng gia đình và bạn bè thân yêu.",
        },
      ],
    },
    details: {
      ceremony: "Lễ Thành Hôn",
      reception: "Tiệc Mừng",
      dateTime: "THỜI GIAN",
      location: "ĐỊA ĐIỂM",
      detailsSoon: "Chủ nhật, 17 tháng 01 năm 2027",
      ceremonyVenue: "White Palace Võ Văn Kiệt",
      receptionVenue: "White Palace Võ Văn Kiệt",
      directionsSoon: "Mở Google Maps",
      directionsUrl: "https://maps.app.goo.gl/nyxm6kKTEt7Vn5RS8",
      receptionNote: "Đón khách và chụp ảnh lúc 17:30. Làm lễ lúc 19:00, dùng tiệc lúc 19:30.",
      dressCode: "Trang Phục",
      dressCodeAccent: "Be hồng · Vàng kem · Đen · Xanh dương trầm",
      dressCodeText:
        "Be hồng, vàng kem, đen hoặc xanh dương trầm. Chọn trang phục bạn thấy thoải mái để cùng chung vui nhé.",
    },
    schedule: {
      heading: "Lịch Trình Ngày Cưới",
      events: [
        {
          time: "17:00",
          title: "Đón Khách",
          description: "Gia đình sẽ đón tiếp khách mời tại White Palace Võ Văn Kiệt.",
        },
        {
          time: "18:00",
          title: "Bắt Đầu Lễ Cưới",
          description: `${coupleNames} sẽ cùng gia đình và bạn bè bắt đầu chương mới đầy ý nghĩa.`,
        },
      ],
    },
    wishes: {
      heading: "Gửi Lời Chúc",
      formTitle: "Gửi Lời Chúc Của Bạn",
      nameLabel: "Tên của bạn",
      namePlaceholder: "Nhập tên của bạn",
      messageLabel: "Lời nhắn",
      messagePlaceholder: "Gửi lời chúc đến cô dâu chú rể...",
      submitIdle: "GỬI LỜI CHÚC",
      submitLoading: "ĐANG GỬI...",
      success: "Cảm ơn bạn. Lời chúc của bạn đã được ghi nhận.",
      searchLabel: "Tìm lời chúc",
      searchPlaceholder: "Tìm theo tên hoặc nội dung",
      loadError: "Hiện chưa thể tải danh sách lời chúc.",
      sendError: "Hiện chưa thể gửi lời chúc.",
      appCheckError:
        "Firebase App Check chưa được cấu hình. Hãy thêm các biến REACT_APP_FIREBASE_* để bật bảo vệ cho phần lời chúc.",
      empty: "Không có lời chúc nào phù hợp với từ khóa tìm kiếm.",
      likeAction: "Yêu thích",
    },
    gallery: {
      heading: "Khoảnh Khắc",
    },
    guestPhotos: {
      heading: "Khoảnh Khắc Từ Khách Mời",
      votePrompt: "Mỗi bức ảnh là một phần câu chuyện của chúng mình. Thả tim để bình chọn khoảnh khắc bạn yêu thích nhé.",
      intro:
        "Mở camera, chụp một khoảnh khắc mới và gửi đến chúng mình ngay.",
      placeholder: "Mở camera để chụp một khoảnh khắc mới trong ngày cưới.",
      openCamera: "MỞ CAMERA",
      takePhoto: "CHỤP ẢNH",
      closeCamera: "ĐÓNG CAMERA",
      sendPhoto: "GỬI ẢNH",
      sending: "ĐANG GỬI...",
      retake: "CHỤP LẠI",
      sendSuccess: "Cảm ơn bạn! Ảnh của bạn đã được thêm vào.",
      loadError: "Hiện chưa thể tải ảnh của khách mời.",
      sendError: "Hiện chưa thể gửi ảnh của bạn.",
      appCheckError:
        "Firebase App Check chưa được cấu hình. Hãy thêm các biến REACT_APP_FIREBASE_* để bật bảo vệ cho phần ảnh khách mời.",
      cameraUnavailable: "Camera không khả dụng trên thiết bị hoặc trình duyệt này.",
      cameraPermissionError:
        "Quyền camera đã bị chặn. Vui lòng cho phép truy cập camera để chụp ảnh.",
      captureError: "Chưa thể chụp ảnh. Vui lòng thử lại.",
      empty: "Chưa có ảnh từ khách mời. Hãy là người đầu tiên chụp một khoảnh khắc.",
      previewAlt: "Ảnh vừa chụp trong ngày cưới",
      guestPhotoAlt: "Khoảnh khắc từ khách mời",
    },
    footer: {
      date: "17 tháng 01 năm 2027",
      location: "White Palace Võ Văn Kiệt",
      message: "Chúng mình và gia đình rất mong được đón tiếp bạn.",
      backToTop: "Lên đầu trang",
      sendWishes: "Gửi lời chúc",
      copyright: `© 2026 ${coupleNames}. All rights reserved.`, // Kepp this in English as it's a legal statement and commonly not translated
    },
  },
} as const;
